import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import AccordionSection from '../AccordionSection';
import CopyButton from '../CopyButton';
import { formatDate } from '../utils';

/**
 * Technical details display component
 */
const TechnicalDetails = ({ result }) => {
  const copyText = useMemo(() => {
    const parts = [];
    
    if (result.integration_status) {
      parts.push(`Integration Status: ${result.integration_status}`);
    }
    if (result.case_id) {
      parts.push(`Case ID: ${result.case_id}`);
    }
    if (result.judge_model) {
      parts.push(`Judge Model: ${result.judge_model}`);
    }
    if (result.response_time_ms != null) {
      parts.push(`Judge Response Time: ${result.response_time_ms < 1000 ? `${result.response_time_ms.toFixed(0)}ms` : `${(result.response_time_ms / 1000).toFixed(2)}s`}`);
    }
    if (result.responded_to_index != null) {
      parts.push(`Target Message Index: ${result.responded_to_index}`);
    }
    if (result.evaluated_at) {
      parts.push(`Evaluated At: ${formatDate(result.evaluated_at)}`);
    }
    if (result.history && Array.isArray(result.history)) {
      parts.push(`Conversation Length: ${result.history.length} message${result.history.length !== 1 ? 's' : ''}`);
    }
    if (result.coach_response) {
      parts.push(`Coach Response Length: ${result.coach_response.length} chars`);
    }
    if (result.evaluation) {
      if (result.evaluation.correctness != null) {
        parts.push(`Correctness: ${(result.evaluation.correctness * 100).toFixed(1)}%`);
      }
      if (result.evaluation.relevance != null) {
        parts.push(`Relevance: ${(result.evaluation.relevance * 100).toFixed(1)}%`);
      }
      if (result.evaluation.guidelines_adherence != null) {
        parts.push(`Guidelines: ${(result.evaluation.guidelines_adherence * 100).toFixed(1)}%`);
      }
      if (result.evaluation.reasoning) {
        parts.push(`Reasoning Length: ${result.evaluation.reasoning.length} chars`);
      }
      if (result.evaluation.issues && Array.isArray(result.evaluation.issues)) {
        parts.push(`Issues Count: ${result.evaluation.issues.length} issue${result.evaluation.issues.length !== 1 ? 's' : ''}`);
      }
    }
    parts.push(`LTM Context: ${result.ltm_context ? '✓' : '✗'}`);
    parts.push(`Conv Summaries: ${result.coach_conversation_summaries ? '✓' : '✗'}`);
    parts.push(`Judge History: ${result.judge_conversation_history ? '✓' : '✗'}`);
    if (result.ltm_context) {
      parts.push(`LTM Context Length: ${result.ltm_context.length} chars`);
    }
    if (result.coach_conversation_summaries) {
      parts.push(`Conversation Summaries Length: ${result.coach_conversation_summaries.length} chars`);
    }
    if (result.judge_conversation_history) {
      parts.push(`Judge History Length: ${result.judge_conversation_history.length} chars`);
    }
    
    return `=== GEEK SECTION ===\n\n${parts.join('\n')}`;
  }, [result]);

  // Preview component showing only Integration Status
  const previewComponent = useMemo(() => {
    const status = result.integration_status || 'Unknown';
    const statusColor = result.integration_status === 'ok'
      ? 'text-emerald-200'
      : 'text-red-200';
    
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-400">Integration Status:</span>
        <span className={`font-mono ${statusColor}`}>{status}</span>
      </div>
    );
  }, [result.integration_status]);

  return (
    <div className="border-t border-gray-800 pt-6">
      <div className="relative">
        <AccordionSection
          title="Geek Section"
          defaultExpanded={false}
          colorTheme="gray"
          previewComponent={previewComponent}
        >
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Integration Status:</span>
              <span
                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${
                  result.integration_status === 'ok'
                    ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40'
                    : 'bg-red-500/15 text-red-200 border-red-500/40'
                }`}
                title="Indicates whether the evaluation system successfully retrieved a response from the Runium coach API. 'ok' = coach responded, 'empty_coach_response' = API returned empty, 'coach_exception' = API call failed"
              >
                {result.integration_status || 'Unknown'}
              </span>
            </div>
          {result.case_id && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Case ID</p>
              <p className="text-sm text-gray-300 font-mono">{result.case_id}</p>
            </div>
          )}
          {result.judge_model && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Judge Model</p>
              <p className="text-sm text-gray-300 font-mono">{result.judge_model}</p>
            </div>
          )}
          {result.response_time_ms != null && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Judge Response Time</p>
              <p className="text-sm text-gray-300 font-mono">
                {result.response_time_ms < 1000 
                  ? `${result.response_time_ms.toFixed(0)}ms` 
                  : `${(result.response_time_ms / 1000).toFixed(2)}s`}
              </p>
            </div>
          )}
          {result.responded_to_index != null && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Target Message Index</p>
              <p className="text-sm text-gray-300 font-mono">{result.responded_to_index}</p>
            </div>
          )}
          {result.evaluated_at && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Evaluated At</p>
              <p className="text-sm text-gray-300 font-mono">
                {formatDate(result.evaluated_at)}
              </p>
            </div>
          )}
          {result.history && Array.isArray(result.history) && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Conversation Length</p>
              <p className="text-sm text-gray-300 font-mono">
                {result.history.length} message{result.history.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
          {result.coach_response && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Coach Response Length</p>
              <p className="text-sm text-gray-300 font-mono">
                {result.coach_response.length} chars
              </p>
            </div>
          )}
          {result.evaluation && (
            <>
              <div>
                <p className="text-xs text-gray-500 mb-1">Score Breakdown</p>
                <div className="text-sm text-gray-300 font-mono space-y-1">
                  {result.evaluation.correctness != null && (
                    <div>Correctness: {(result.evaluation.correctness * 100).toFixed(1)}%</div>
                  )}
                  {result.evaluation.relevance != null && (
                    <div>Relevance: {(result.evaluation.relevance * 100).toFixed(1)}%</div>
                  )}
                  {result.evaluation.guidelines_adherence != null && (
                    <div>Guidelines: {(result.evaluation.guidelines_adherence * 100).toFixed(1)}%</div>
                  )}
                </div>
              </div>
              {result.evaluation.reasoning && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Reasoning Length</p>
                  <p className="text-sm text-gray-300 font-mono">
                    {result.evaluation.reasoning.length} chars
                  </p>
                </div>
              )}
              {result.evaluation.issues && Array.isArray(result.evaluation.issues) && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Issues Count</p>
                  <p className="text-sm text-gray-300 font-mono">
                    {result.evaluation.issues.length} issue{result.evaluation.issues.length !== 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </>
          )}
          <div>
            <p className="text-xs text-gray-500 mb-1">Context Flags</p>
            <div className="text-sm text-gray-300 font-mono space-y-1">
              <div>LTM Context: {result.ltm_context ? '✓' : '✗'}</div>
              <div>Conv Summaries: {result.coach_conversation_summaries ? '✓' : '✗'}</div>
              <div>Judge History: {result.judge_conversation_history ? '✓' : '✗'}</div>
            </div>
          </div>
          {result.ltm_context && (
            <div>
              <p className="text-xs text-gray-500 mb-1">LTM Context Length</p>
              <p className="text-sm text-gray-300 font-mono">
                {result.ltm_context.length} chars
              </p>
            </div>
          )}
          {result.coach_conversation_summaries && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Conversation Summaries Length</p>
              <p className="text-sm text-gray-300 font-mono">
                {result.coach_conversation_summaries.length} chars
              </p>
            </div>
          )}
          {result.judge_conversation_history && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Judge History Length</p>
              <p className="text-sm text-gray-300 font-mono">
                {result.judge_conversation_history.length} chars
              </p>
            </div>
          )}
          </div>
        </AccordionSection>
        <CopyButton
          onCopy={copyText}
          ariaLabel="Copy technical details"
          title="Copy technical details"
          colorTheme="gray"
        />
      </div>
    </div>
  );
};

TechnicalDetails.propTypes = {
  result: PropTypes.object.isRequired,
};

export default React.memo(TechnicalDetails);

